/**
 * The lot rail's "Kept in": a "Save to list" button (the same control as the
 * register card's Save) that opens the lists with ticks, a search when there
 * are many, and "Create a list" which also adds the lot; the lists the lot is
 * kept in as chips; and the personal "Visited" mark.
 * Ported from the workbench's PropertyListMenu and PropertyVisitedControl.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { IconBookmark, IconBookmarkFilled, IconCheck, IconChevronDown, IconPlus } from '@tabler/icons-react'
import { ApiError } from '../../api/http'
import { useCreateList, useListsForProperty, useSetListMembership, useSetVisited } from '../../api/queries'
import { usePersonalAccess, usePersonalSettings } from '../../api/personalSettings'
import type { PersonalSettings } from '../../api/types'
import { Button, Popover, TextInput } from '../../ui/kit'

/** Opening the lot records the visit; the mark lights up at once and is never toggled by hand. */
function VisitedMark({ propertyId, snapshot }: { propertyId: string; snapshot: PersonalSettings }) {
  const access = usePersonalAccess()
  const query = usePersonalSettings(propertyId, snapshot)
  const save = useSetVisited()
  const current = query.data ?? snapshot
  const marked = useRef<string | null>(null)
  useEffect(() => {
    if (!access.write || current.visited || marked.current === propertyId) return
    marked.current = propertyId
    save.mutate({ propertyId, visited: true, expectedVisitRevision: current.visitRevision }, {
      // A newer revision elsewhere: read it back rather than fight it.
      onError: error => { if (error instanceof ApiError && error.status === 409) void query.refetch() },
    })
  }, [access.write, current.visited, current.visitRevision, propertyId, save, query])
  if (!access.read) return null
  const lit = current.visited || save.isPending || save.isSuccess
  return <div className="kept-visited">
    <span className={lit ? 'vtag is-lit' : 'vtag is-off'} role="status">{lit ? <IconCheck className="ic-sm" aria-hidden="true" /> : null}{lit ? 'Visited' : 'Not visited'}</span>
    {save.isError && !(save.error instanceof ApiError && save.error.status === 409) ? <span className="fld-error" role="alert">Visit could not be recorded: {save.error.message}</span> : null}
  </div>
}

export function KeptInPanel({ propertyId, personal }: { propertyId: string; personal: PersonalSettings | null }) {
  const lists = useListsForProperty(propertyId)
  const create = useCreateList()
  const membership = useSetListMembership()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const all = lists.data ?? []
  const saved = all.filter(list => list.containsProperty)
  const busy = membership.isPending || create.isPending
  const needle = search.trim().toLowerCase()
  const visible = needle ? all.filter(list => list.name.toLowerCase().includes(needle)) : all

  const createAndAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const cleanName = name.trim()
    if (!cleanName) return
    try {
      const list = await create.mutateAsync({ name: cleanName })
      await membership.mutateAsync({ listId: list.id, propertyId, member: true })
      setName('')
      setCreating(false)
    } catch { /* the error stays beside the form */ }
  }
  const close = () => { setOpen(false); setCreating(false); setSearch('') }

  return <section className="rail-sec kept">
    <div className="rail-sh"><span className="t-section">Kept in</span><span className="hint">{saved.length ? `${saved.length} ${saved.length === 1 ? 'list' : 'lists'}` : 'not saved'}</span></div>
    <Popover open={open} onClose={close} align="start" width={280} anchor={
      <button type="button" className={['btn', 'kept-save', saved.length ? 'is-on' : 'is-primary'].join(' ')} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} aria-busy={busy}>
        {saved.length ? <IconBookmarkFilled className="ic" aria-hidden="true" /> : <IconBookmark className="ic" aria-hidden="true" />}
        <span>{saved.length ? `Saved to ${saved.length === 1 ? saved[0]!.name : `${saved.length} lists`}` : 'Save to list'}</span>
        <IconChevronDown className="ic-sm" aria-hidden="true" />
      </button>
    }>
      <div className="pop-title">Save to a list</div>
      {all.length > 6 ? <input className="pop-search" placeholder="Search lists" value={search} onChange={e => setSearch(e.target.value)} autoFocus aria-label="Search lists" /> : null}
      <div className="pop-list" role="listbox" aria-multiselectable="true" aria-label="Lists">
        {lists.isPending ? <span className="t-muted pop-empty">Loading lists…</span> : null}
        {lists.isError ? <span className="fld-error pop-empty">Could not load lists: {lists.error.message}</span> : null}
        {lists.data && all.length === 0 ? <span className="t-muted pop-empty">No lists yet. Create one below.</span> : null}
        {visible.map(list => (
          <button type="button" role="option" aria-selected={list.containsProperty} key={list.id} className={['is-check', list.containsProperty ? 'is-on' : ''].join(' ')} disabled={busy}
            onClick={() => membership.mutate({ listId: list.id, propertyId, member: !list.containsProperty })}>
            <span className={['chk-box', list.containsProperty ? 'is-on' : ''].join(' ')} aria-hidden="true">{list.containsProperty ? <IconCheck className="ic-sm" /> : null}</span>
            <span className="opt-main"><span className="opt-label">{list.name}</span></span>
            <span className="count">{list.propertyCount}</span>
          </button>
        ))}
        {needle && visible.length === 0 && all.length ? <span className="t-faint pop-empty">No matches</span> : null}
      </div>
      <div className="pop-foot">
        {membership.isError ? <span className="fld-error" role="alert">{membership.error.message}</span> : null}
        {creating ? <form className="kept-create" onSubmit={createAndAdd}>
          <TextInput value={name} onChange={setName} placeholder="New list name" maxLength={80} autoFocus ariaLabel="New list name" />
          <div className="form-row">
            <Button small onClick={() => setCreating(false)}>Cancel</Button>
            <Button small kind="primary" type="submit" disabled={!name.trim() || busy}>Create and add</Button>
          </div>
          {create.isError ? <span className="fld-error" role="alert">{create.error.message}</span> : null}
        </form> : <button type="button" className="btn is-small is-accent" onClick={() => setCreating(true)}><IconPlus className="ic-sm" aria-hidden="true" />Create a list</button>}
        <span className="t-muted">Tick a list to keep this lot in it.</span>
      </div>
    </Popover>
    {saved.length ? <div className="kept-chips">{saved.map(list => <span className="chip" key={list.id} title={`${list.propertyCount} properties`}>{list.name}</span>)}</div> : null}
    {personal ? <VisitedMark propertyId={propertyId} snapshot={personal} /> : null}
  </section>
}
